import { TrendLabel } from '@/components/customComponents'
import { Card, CardContent } from '@/components/ui/card'
import {
  RadioGroupButton,
  RadioGroupButtonItem,
} from '@/components/ui/RadioGroup'
import { Separator } from '@/components/ui/separator'
import { REGION_CARD_COLOR_CLASS_MAP } from '@/constants/METRIC_CARD_ICONS'
import { IAbstractBasicRegionMetric, IAbstractRegionMetric } from '@/types'

import { cn, formatNumber } from '@/utils'
import {
  getFormattedMetricString,
  getMetricIsIncreasingPositive,
  getMetricLabel,
  getMetricType,
} from '@/utils/labels'

interface ScalrBoxCardProps<T> {
  data: T
  icon: string
  color: string
  cardOnClick: (name: string) => void
  isParent?: boolean
  selectedMetric?: string | null
  selectedCardId?: string | null
  onMetricSelect?: (metricValue: string, cardId: string) => void
  cardId?: string
  selected?: boolean
  showKpi?: boolean
}

interface ScalrBoxMetricsProps<T> {
  data: IAbstractRegionMetric<T>
  isParent?: boolean
  selectedMetric?: string | null
  selectedCardId?: string | null
  onMetricSelect?: (metricValue: string, cardId: string) => void
  cardId?: string
  showKpi?: boolean
}

export default function ScalrBoxCard<T extends IAbstractBasicRegionMetric>({
  data,
  icon,
  color,
  cardOnClick,
  isParent = false,
  selectedMetric = null,
  selectedCardId = null,
  onMetricSelect = () => {},
  cardId = '',
  selected = false,
  showKpi = true,
}: ScalrBoxCardProps<T>) {
  const colorClasses =
    REGION_CARD_COLOR_CLASS_MAP[color] ||
    REGION_CARD_COLOR_CLASS_MAP['blue-600']

  return (
    <Card
      className={cn(
        'cursor-pointer',
        isParent ? colorClasses.hover : '',
        selected && `${colorClasses.bg} border-2 ${colorClasses.border}`
      )}
      onClick={() => {
        cardOnClick(data.stand || '')
      }}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <img src={icon} alt="metric-card-icon" />
          <span className="leading-none tracking-tight text-black-500 text-display-s">
            {data.stand}
          </span>
        </div>

        <ScalrBoxMetrics
          data={data}
          isParent={isParent}
          selectedMetric={selectedMetric}
          selectedCardId={selectedCardId}
          onMetricSelect={onMetricSelect}
          cardId={cardId}
          showKpi={showKpi}
        />
      </CardContent>
    </Card>
  )
}

function ScalrBoxMetrics<T extends IAbstractRegionMetric<T>>({
  data,
  isParent,
  selectedMetric,
  selectedCardId,
  onMetricSelect,
  cardId,
  showKpi,
}: ScalrBoxMetricsProps<T>) {
  if (!data || !data.current) return null
  const firstKey = Object.keys(data.current)[0] as keyof T
  const oldValue = data.prev ? (data.prev[firstKey] as number) : 0
  const newValue = data.current[firstKey] as number

  // Only consider a card selected if it's not a parent card and its ID matches the selected card ID
  const isCardSelected = !isParent && selectedCardId === cardId

  // Create a combined value that includes both the metric type and card ID
  const selectedValue =
    isCardSelected && selectedMetric ? `${selectedMetric}-${cardId}` : ''

  const handleMetricChange = (value: string) => {
    if (onMetricSelect && !isParent) {
      // Extract just the metric part (before the hyphen)
      const metricType = value.split('-')[0]
      onMetricSelect(metricType, cardId || '')
    }
  }

  return (
    <div>
      <div>
        <div className="flex justify-between">
          <div className="text-text-m text-gray-500">
            {getMetricLabel(firstKey)}
          </div>
          <div className="text-text-m text-gray-500">vs KPI</div>
        </div>
        <div className="flex justify-between">
          <div className="flex items-center gap-1">
            <span className="text-lg font-semibold">
              {getFormattedMetricString(firstKey, newValue)}
            </span>
            <TrendLabel
              oldValue={oldValue}
              newValue={newValue || 0}
              isNormal={getMetricIsIncreasingPositive(firstKey)}
              trendType={
                getMetricType(firstKey) as
                  | 'money'
                  | 'percentage'
                  | 'number'
                  | 'hour'
              }
            />
          </div>
          {showKpi && (
            <div className="flex items-center gap-1">
              <span className="text-lg font-semibold">0$</span>
              {/* <TrendLabel oldValue={100} newValue={95} isNormal={true} /> */}
              -
            </div>
          )}
        </div>
      </div>
      <Separator className="my-2" />

      {isParent ? (
        <div
          className={`grid grid-cols-${Object.keys(data.current).length - 1} gap-1 text-sm`}
        >
          {Object.keys(data.current)
            .slice(1)
            .map((_key, index) => {
              if (!data.current) return null
              const key = _key as keyof T
              const oldVal = data.prev ? (data.prev[key] as number) : 0
              const newVal = data.current[key] as number

              return (
                <Card key={index} className="flex flex-col bg-inherit">
                  <span className="text-text-m text-gray-500 text-center mb-1">
                    {getMetricLabel(key)}
                  </span>
                  <div className="flex justify-between items-center gap-1 w-full px-3 pb-1">
                    <span className="font-medium">
                      {formatNumber(
                        newVal,
                        getMetricType(key) as
                          | 'money'
                          | 'percentage'
                          | 'number'
                          | 'hour'
                      )}
                    </span>
                    <TrendLabel
                      oldValue={oldVal}
                      newValue={newVal}
                      isNormal={getMetricIsIncreasingPositive(key)}
                      trendType={
                        getMetricType(key) as
                          | 'money'
                          | 'percentage'
                          | 'number'
                          | 'hour'
                      }
                      isBold={true}
                      verySmall={true}
                    />
                  </div>
                </Card>
              )
            })}
        </div>
      ) : (
        <div className="flex justify-center">
          <RadioGroupButton
            value={selectedValue}
            onValueChange={handleMetricChange}
          >
            <div
              className={`grid grid-cols-${Object.keys(data.current).length - 1} gap-1 text-sm`}
            >
              {Object.keys(data.current)
                .slice(1)
                .map((_key, index) => {
                  if (!data.current) return null
                  const key = _key as keyof T
                  const oldVal = data.prev ? (data.prev[key] as number) : 0
                  const newVal = data.current[key] as number
                  return (
                    <RadioGroupButtonItem
                      key={index}
                      value={`${String(key)}-${cardId}`}
                      id={`${String(key)}-${cardId}`}
                      className="p-0 rounded-xl h-[50px] w-[120px]"
                    >
                      <div className="flex flex-col">
                        <span className="text-text-m text-gray-500 text-center mb-1">
                          {getMetricLabel(key)}
                        </span>
                        <div className="flex justify-between items-center gap-1 w-full px-3">
                          <span className="font-medium">
                            {formatNumber(
                              newVal,
                              getMetricType(key) as
                                | 'money'
                                | 'percentage'
                                | 'number'
                                | 'hour'
                            )}
                          </span>
                          <TrendLabel
                            oldValue={oldVal}
                            newValue={newVal}
                            isNormal={true}
                            trendType={
                              getMetricType(key) as
                                | 'money'
                                | 'percentage'
                                | 'number'
                                | 'hour'
                            }
                            isBold={true}
                            verySmall={true}
                          />
                        </div>
                      </div>
                    </RadioGroupButtonItem>
                  )
                })}
            </div>
          </RadioGroupButton>
        </div>
      )}
    </div>
  )
}
